export const DEFAULT_FOOD_IMAGE = '/assets/img/tsr-default.JPG';
export const PERSON_OPTIONS = [10, 20, 30, 40, 50, 75, 100];
export const SERVING_UNITS = ['pcs', 'bowl', 'plate', 'gm', 'ml', 'kg', 'litre', 'glass', 'cup', 'scoop'];

export const DEFAULT_QUANTITY_ROUND_RULES = [
  { ServingUnit: 'kg', RoundStep: 0.5, RoundMode: 'ceil' },
  { ServingUnit: 'gm', RoundStep: 500, RoundMode: 'ceil' },
  { ServingUnit: 'litre', RoundStep: 0.5, RoundMode: 'ceil' },
  { ServingUnit: 'ml', RoundStep: 500, RoundMode: 'ceil' },
  { ServingUnit: 'pcs', RoundStep: 1, RoundMode: 'ceil' },
  { ServingUnit: 'plate', RoundStep: 1, RoundMode: 'ceil' },
  { ServingUnit: 'bowl', RoundStep: 1, RoundMode: 'ceil' },
  { ServingUnit: 'glass', RoundStep: 1, RoundMode: 'ceil' },
  { ServingUnit: 'scoop', RoundStep: 1, RoundMode: 'ceil' }
];

export const resolveFoodImage = (item) => {
  const fromDb = item?.ItemImageUrl || item?.ImageUrl || item?.ItemImage || item?.PackageImageUrl || '';
  return String(fromDb).trim() || DEFAULT_FOOD_IMAGE;
};

export const toBool = (value) => {
  if (value && typeof value === 'object' && value.type === 'Buffer') {
    return value.data?.[0] === 1;
  }
  return value === true || value === 1 || value === '1';
};

export const toBooleanFromBit = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1') return true;
  if (value === 0 || value === '0') return false;
  if (typeof value === 'object') {
    if (value.type === 'Buffer' && Array.isArray(value.data)) {
      return value.data[0] === 1;
    }
    if (typeof value[0] === 'number') {
      return value[0] === 1;
    }
  }
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes'].includes(normalized)) return true;
  if (['0', 'false', 'no'].includes(normalized)) return false;
  return defaultValue;
};

export const resolveHeadcounts = (persons, kidPersons = 0) => {
  const total = Math.max(0, Number(persons) || 0);
  const kids = Math.min(Math.max(0, Number(kidPersons) || 0), total);
  return {
    total,
    kids,
    adults: total - kids
  };
};

export const getItemQtyPerAdult = (item) => {
  const adult = item?.qtyPerAdult ?? item?.QtyPerAdult;
  if (adult != null && adult !== '' && Number(adult) > 0) {
    return Number(adult);
  }
  return Number(item?.qtyPerPerson ?? item?.QtyPerPerson) || 0;
};

export const getItemQtyPerKid = (item) => {
  const kid = item?.qtyPerKid ?? item?.QtyPerKid;
  if (kid == null || kid === '') {
    return 0;
  }
  return Math.max(0, Number(kid) || 0);
};

export const calcRawTotalQuantity = (item, persons, kidPersons = 0) => {
  const { adults, kids } = resolveHeadcounts(persons, kidPersons);
  const qtyPerAdult = getItemQtyPerAdult(item);
  const qtyPerKid = getItemQtyPerKid(item);
  if (item.scaleByPersons === false) {
    return qtyPerAdult;
  }
  return (qtyPerAdult * adults) + (qtyPerKid * kids);
};

export const roundProductionQuantity = (rawQuantity, servingUnit, rules = []) => {
  const raw = Number(rawQuantity) || 0;
  const unit = String(servingUnit || 'pcs').trim().toLowerCase();
  const rule = (rules || []).find(
    (entry) => String(entry.ServingUnit || '').trim().toLowerCase() === unit
  );
  const step = rule?.RoundStep != null ? Number(rule.RoundStep) : 1;
  const mode = String(rule?.RoundMode || 'ceil').trim().toLowerCase();
  if (!step || step <= 0) {
    return raw;
  }
  const units = raw / step;
  if (mode === 'nearest') {
    return Math.round(units) * step;
  }
  return Math.ceil(units) * step;
};

export const calcProductionTotalQuantity = (item, persons, kidPersons = 0, rules = []) => {
  const raw = calcRawTotalQuantity(item, persons, kidPersons);
  return roundProductionQuantity(raw, item.servingUnit, rules);
};

/** @deprecated use calcRawTotalQuantity for billing; kept for older screens */
export const calcTotalQuantity = (item, persons, kidPersons = 0) => (
  calcRawTotalQuantity(item, persons, kidPersons)
);

export const calcLineTotal = (item, persons, kidPersons = 0) => (
  calcRawTotalQuantity(item, persons, kidPersons) * (Number(item.rate) || 0)
);

export const formatQty = (value) => {
  const num = Number(value) || 0;
  return String(Math.round(num));
};

export const formatQtyDisplay = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  });
};

export const formatQtyWithUnit = (value, unit = 'pcs') => {
  const normalizedUnit = String(unit || 'pcs').trim() || 'pcs';
  return `${formatQtyDisplay(value)} ${normalizedUnit}`;
};

export const formatAmount = (value) => {
  return Number(value || 0).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const mapPackageDetailToState = (data) => {
  const pkg = data?.package;
  const items = data?.items || [];

  if (!pkg) {
    return null;
  }

  return {
    foodPackageId: pkg.FoodPackageId,
    packageName: pkg.PackageName,
    packageImageUrl: resolveFoodImage(pkg),
    persons: Number(pkg.Persons) || 10,
    kidPersons: Number(pkg.KidPersons) || 0,
    notes: pkg.Notes || '',
    items: items.map((item) => {
      const qtyPerAdult = Number(item.QtyPerAdult) > 0
        ? Number(item.QtyPerAdult)
        : Number(item.QtyPerPerson) || 1;
      return {
        foodItemId: item.FoodItemId,
        name: item.ItemName,
        category: item.CategoryName,
        rate: Number(item.Rate) || 0,
        qtyPerAdult,
        qtyPerKid: Number(item.QtyPerKid) || 0,
        qtyPerPerson: qtyPerAdult,
        servingUnit: item.ServingUnit || 'pcs',
        scaleByPersons: toBool(item.ScaleByPersons),
        rawTotalQuantity: item.RawTotalQuantity != null ? Number(item.RawTotalQuantity) : null,
        productionTotalQuantity: item.TotalQuantity != null ? Number(item.TotalQuantity) : null
      };
    })
  };
};

export const mapPackageDetailToAdminState = (data, foodItems = []) => {
  const mapped = mapPackageDetailToState(data);
  if (!mapped) {
    return null;
  }

  const catalogById = new Map(
    (foodItems || []).map((item) => [item.id ?? item.FoodItemId, item])
  );

  return {
    editingPackageId: mapped.foodPackageId,
    packageImageUrl: mapped.packageImageUrl,
    packageForm: {
      packageName: mapped.packageName,
      persons: mapped.persons,
      kidPersons: mapped.kidPersons,
      notes: mapped.notes
    },
    selectedItems: mapped.items.map((item) => {
      const catalogItem = catalogById.get(item.foodItemId);
      return {
        id: item.foodItemId,
        name: item.name,
        rate: item.rate,
        qtyPerAdult: item.qtyPerAdult,
        qtyPerKid: item.qtyPerKid,
        qtyPerPerson: item.qtyPerPerson,
        servingUnit: item.servingUnit,
        scaleByPersons: item.scaleByPersons,
        category: catalogItem?.category || item.category || '',
        subCategory: catalogItem?.subCategory || '',
        description: catalogItem?.description || '',
        image: catalogItem?.image || resolveFoodImage({ ItemName: item.name })
      };
    })
  };
};
